import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Bell, Search, Plus, X, HelpCircle, PanelLeft, ChevronDown,
    BookOpen, Rocket, MessageSquareText,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { HELP_BY_PATH } from '../data/helpContent';
import { getActiveItemLabel } from '../config/workspaces';
import { NOTIFICATIONS_CHANGED_EVENT } from '../lib/bellLinkage';
import { inboxTrashLink } from '../components/inbox/inboxDeepLink';
import useWikiCrumb from '../pages/wiki/shared/useWikiCrumb';
import useWikiRole from '../pages/wiki/shared/useWikiRole';
import { WIKI_TOASTS, wikiUi } from '../pages/wiki/shared/wikiUi';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// #545 — documented bell refresh interval (badge poll). The list is
// fetched lazily when the dropdown opens, not on the timer.
const NOTIFICATIONS_POLL_MS = 60000;

const HEALTH_WORD = {
    healthy: 'Operational',
    degraded: 'Degraded',
    down: 'Down',
    unknown: 'Status unknown',
};

const HEALTH_DOT = {
    healthy: 'var(--good)',
    degraded: 'var(--warn)',
    down: 'var(--bad)',
    unknown: 'var(--ink-3)',
};

export default function TopBar({
    onOpenPalette,
    onMenuToggle,
    onOpenHub,
    onToggleSidebar,
    sidebarCollapsed,
    getHealthStatus,
}) {
    const { user, getAuthHeader } = useAuth();
    const { currentWorkspace, setCurrentWorkspace, isItemVisible: isWsItemVisible } = useWorkspace();
    const navigate = useNavigate();
    const location = useLocation();

    const [notifications, setNotifications] = useState([]);
    // #715 — the bell view's overflow facts: {unread_total, unread_returned,
    // truncated}. The server selects every unread entry (≤200) plus read ones
    // from the last 7 days; `truncated` says honestly when there are more.
    const [bellMeta, setBellMeta] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);
    // "ok" | "degraded" — degraded suppresses the badge (never render a
    // number the backend could not substantiate) and offers a refresh.
    const [notifStatus, setNotifStatus] = useState('ok');
    const [showNotifs, setShowNotifs] = useState(false);
    const [showMobileSearch, setShowMobileSearch] = useState(false);
    const [showAddMenu, setShowAddMenu] = useState(false);
    const [showHelpMenu, setShowHelpMenu] = useState(false);

    const addRef = useRef(null);
    const helpRef = useRef(null);
    const notifRef = useRef(null);

    // #715 audit F12 — a count or list request that was in flight while a
    // local mark-read started or settled answers with the PRE-write number,
    // and applying it would briefly restore a badge the user just cleared.
    // Every write bumps this epoch when it starts and again when it settles;
    // a response whose request began in an older epoch is dropped (the next
    // poll or change event brings the truth).
    const writeEpochRef = useRef(0);
    const bumpWriteEpoch = () => { writeEpochRef.current += 1; };

    const fetchUnread = useCallback(async () => {
        const epoch = writeEpochRef.current;
        try {
            const res = await axios.get(`${API}/notifications/unread-count`, { headers: getAuthHeader() });
            if (epoch !== writeEpochRef.current) return;
            setUnreadCount(Number(res.data && res.data.unread_count) || 0);
            setNotifStatus('ok');
        } catch {
            setUnreadCount(null);
            setNotifStatus('degraded');
        }
    }, [getAuthHeader]);

    const fetchNotifs = useCallback(async () => {
        const epoch = writeEpochRef.current;
        try {
            const res = await axios.get(`${API}/notifications?view=bell`, { headers: getAuthHeader() });
            if (epoch !== writeEpochRef.current) return;
            const data = res.data || {};
            setNotifications(Array.isArray(data.items) ? data.items : []);
            setBellMeta({
                unread_total: Number(data.unread_total) || 0,
                unread_returned: Number(data.unread_returned) || 0,
                truncated: data.truncated === true,
            });
            // #715 — the list's unread_total is the same filter as the badge
            // count, so the two surfaces share one number the moment the
            // dropdown opens instead of disagreeing until the next poll.
            if (typeof data.unread_total === 'number') setUnreadCount(data.unread_total);
            // Note: a list success does NOT clear a degraded status — only
            // a successful badge COUNT does. Degraded means "the bell
            // cannot substantiate its surfaces"; Refresh retries both.
        } catch {
            setNotifStatus('degraded');
        }
    }, [getAuthHeader]);

    useEffect(() => {
        fetchUnread();
        const interval = setInterval(fetchUnread, NOTIFICATIONS_POLL_MS);
        return () => clearInterval(interval);
    }, [fetchUnread]);

    // #715 — a reader cleared bell entries (opened a thread, marked mail
    // read); refetch so the badge drops without waiting up to
    // NOTIFICATIONS_POLL_MS for the next poll.
    useEffect(() => {
        const onChanged = () => {
            fetchUnread();
            if (showNotifs) fetchNotifs();
        };
        window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
        return () => window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
    }, [fetchUnread, fetchNotifs, showNotifs]);

    // Close open dropdowns on outside click
    useEffect(() => {
        const handler = (e) => {
            if (addRef.current && !addRef.current.contains(e.target)) setShowAddMenu(false);
            if (helpRef.current && !helpRef.current.contains(e.target)) setShowHelpMenu(false);
            if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const markAllRead = async () => {
        bumpWriteEpoch();
        try {
            await axios.put(`${API}/notifications/read-all`, {}, { headers: getAuthHeader() });
            bumpWriteEpoch(); // the refetch below starts after the write settled
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
            fetchNotifs();
        } catch (err) {
            // F05 (audit): never silent — but non-fatal. The badge poll is the
            // source of truth and self-heals within 60 s regardless.
            bumpWriteEpoch();
            console.warn('#545 bell: mark-all-read failed — badge self-heals on the next poll', err);
        }
    };

    const toggleNotifs = () => {
        const next = !showNotifs;
        setShowNotifs(next);
        if (next) fetchNotifs(); // list is fetched lazily on open
    };

    const handleNotifClick = (n) => {
        setShowNotifs(false);
        if (!n.is_read) {
            setUnreadCount(u => (typeof u === 'number' ? Math.max(0, u - 1) : u));
            setNotifications(prev => prev.map(x => (
                x.notification_id === n.notification_id ? { ...x, is_read: true } : x)));
            bumpWriteEpoch();
            axios.put(`${API}/notifications/${n.notification_id}/read`, {}, { headers: getAuthHeader() })
                .finally(bumpWriteEpoch)
                .catch((err) => {
                    // F05 (audit): fire-and-forget read is non-fatal — the next
                    // badge poll reconciles the count within 60 s.
                    console.warn('#545 bell: mark-one-read failed — badge self-heals on the next poll', err);
                });
        }
        const ref = n.ref || {};
        if (n.type === 'email' && n.mail_state === 'trashed' && ref.in_trash_of) {
            // #803 D1 — a shared inbox's binned mail has no openable thread
            // (the thread endpoint requires the inbox tag), so its Trash list
            // is the honest target. A personal trashed copy still opens by
            // comm_id below.
            navigate(inboxTrashLink(ref.in_trash_of));
        } else if (n.type === 'email') {
            // Two lanes, two routes. A unified/departmental row carries the
            // inbox it matched; a PERSONAL-lane row has no inbox_ids at all
            // (the producer falls back to the row's owner), so it only ever
            // has comm_id — and /inbox/email/:commId is that lane's reader.
            // Without this branch every personal email notification dropped
            // the user on the inbox root instead of the message they clicked.
            //
            // #715 — each entry is ONE message, and a thread reader opens at
            // the newest message by default, so the clicked message is handed
            // over as `focusCommId` for the reader to scroll to.
            const focus = { state: { focusCommId: ref.comm_id || null } };
            if (ref.inbox_id && ref.conversation_id) navigate(`/inbox/${ref.inbox_id}/${ref.conversation_id}`, focus);
            else if (ref.comm_id) navigate(`/inbox/email/${ref.comm_id}`, focus);
            else navigate('/inbox');
        } else if (n.type === 'task') {
            navigate('/tasks'); // Tasks.jsx has no deep-link param — the list is the target
        } else if (n.link) {
            // db.notifications is shared: the campaign "notification" step,
            // the agentic-email review notice and the feature-request follower
            // notice all write rows whose type is none of the above and which
            // carry their own destination in `link` (/ai-reviews,
            // /contacts/:id). Without this they closed the dropdown and went
            // nowhere. Relative in-app paths only — an absolute URL in a
            // stored row must not become an open redirect through navigate().
            if (n.link.startsWith('/') && !n.link.startsWith('//')) navigate(n.link);
        }
    };

    // In the Wiki the crumb follows the prototype (spec A §1.4): Home / Ask
    // the Wiki / To acknowledge / AI Brain, the page title on a page, and
    // "Category" or "Category / Subcategory" on a category — names come from
    // what the Wiki already cached, never a fetch of the bar's own.
    const wikiCrumb = useWikiCrumb(location.pathname);
    const wikiRole = useWikiRole();
    const activeLabel = useMemo(
        () => (wikiCrumb.isWiki ? wikiCrumb.label : getActiveItemLabel(currentWorkspace, location.pathname)),
        [wikiCrumb, currentWorkspace, location.pathname]
    );

    // Wiki "New" (prototype `topNew`): editors and Wiki admins get the New
    // page modal defaulted to the open category/subcategory/page location;
    // Members get the prototype's toast. Nothing happens until the role is
    // known, so a slow permissions call never shows an editor the denial.
    const onWikiNew = () => {
        if (wikiRole.loading) return;
        if (!wikiRole.canEdit) {
            wikiUi.toast(WIKI_TOASTS.memberCannotCreate);
            return;
        }
        wikiUi.openModal('page', { mode: 'write', ...(wikiCrumb.location || {}) });
    };

    const showModuleChip = !!(currentWorkspace.type === 'module-iframe' || currentWorkspace.providerId);
    const health = currentWorkspace.providerId && typeof getHealthStatus === 'function'
        ? getHealthStatus(currentWorkspace.providerId)
        : 'unknown';

    const visibleQuickActions = useMemo(() => {
        const actions = currentWorkspace.quickActions || [];
        return actions.filter(a => (typeof isWsItemVisible === 'function' ? isWsItemVisible(a, user) : true));
    }, [currentWorkspace, isWsItemVisible, user]);

    const helpSlug = HELP_BY_PATH[location.pathname] || HELP_BY_PATH['/' + location.pathname.split('/')[1]] || '';
    const helpTarget = helpSlug ? `/help#${helpSlug}` : '/help';

    const goWorkspaceHome = () => {
        // First item of the first section (falls back to no-op)
        const first = currentWorkspace.sections?.[0]?.items?.[0];
        if (first?.path) navigate(first.path.split('?')[0]);
    };

    return (
        <header
            className="h-[52px] flex items-center px-3 sm:px-4 gap-2 sm:gap-3 sticky top-0 z-30"
            style={{
                background: 'var(--glass)',
                backdropFilter: 'blur(14px)',
                WebkitBackdropFilter: 'blur(14px)',
                borderBottom: '1px solid var(--line)',
                color: 'var(--ink-1)',
            }}
        >
            {/* Mobile hamburger */}
            <button
                onClick={onMenuToggle}
                className="p-2 rounded-lg hover:bg-black/5 text-ink-2 md:hidden"
                data-testid="mobile-menu-btn"
                aria-label="Toggle sidebar"
            >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path d="M3 6h14M3 10h14M3 14h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
            </button>

            {/* Sidebar collapse toggle (≥md) */}
            <button
                onClick={onToggleSidebar}
                data-testid="sidebar-collapse-btn"
                className="hidden md:flex p-2 rounded-lg hover:bg-black/5 text-ink-2 items-center"
                aria-label={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
                aria-pressed={sidebarCollapsed}
            >
                <PanelLeft size={18} />
            </button>

            {/* Crumb: workspace / page */}
            <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 min-w-0">
                <button
                    onClick={goWorkspaceHome}
                    title={`Go to ${currentWorkspace.name} home`}
                    className="px-1.5 py-1 rounded-md hover:bg-black/5 text-ink-1 text-[13px] font-semibold truncate"
                    style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}
                >
                    {currentWorkspace.name}
                </button>
                {activeLabel && (
                    <>
                        <span className="text-ink-3 text-xs" aria-hidden="true">/</span>
                        <span
                            className="text-ink-2 text-[13px] font-medium truncate max-w-[220px]"
                            title={activeLabel}
                        >
                            {activeLabel}
                        </span>
                    </>
                )}
            </nav>

            {/* Module chip */}
            {showModuleChip && (
                <div
                    className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full ml-1 border"
                    style={{ borderColor: 'var(--line)' }}
                    title={`${currentWorkspace.name} — ${HEALTH_WORD[health] || HEALTH_WORD.unknown}`}
                >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: HEALTH_DOT[health] || HEALTH_DOT.unknown }} aria-hidden="true" />
                    <span className="text-[11px] font-semibold text-ink-2">{HEALTH_WORD[health]}</span>
                </div>
            )}

            <div className="flex-1" />

            {/* Search field (opens palette) */}
            <button
                onClick={onOpenPalette}
                className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg text-ink-label text-sm min-w-[220px] lg:min-w-[300px] border transition-colors hover:border-ink-2"
                style={{
                    background: 'color-mix(in srgb, var(--ink-1) 3%, transparent)',
                    borderColor: 'var(--line)',
                }}
                data-testid="search-trigger"
            >
                <Search className="w-4 h-4" />
                <span>Search or jump to…</span>
                <span
                    className="ml-auto px-1.5 py-0.5 rounded text-[10px] font-mono border"
                    style={{
                        background: 'var(--surface)',
                        borderColor: 'var(--line)',
                        color: 'var(--ink-2)',
                    }}
                >
                    ⌘K
                </span>
            </button>

            {/* Mobile search icon */}
            <button
                onClick={() => setShowMobileSearch(!showMobileSearch)}
                className="p-2 rounded-lg hover:bg-black/5 text-ink-2 sm:hidden"
                aria-label="Open search"
            >
                <Search className="w-5 h-5" />
            </button>

            {/* Wiki: + New opens the New page modal (no menu, despite the
                chevron — prototype parity). Same button as the menu trigger. */}
            {wikiCrumb.isWiki && (
                <button
                    type="button"
                    onClick={onWikiNew}
                    data-testid="wiki-new-btn"
                    className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 text-sm font-medium rounded-lg shadow-sm transition-colors"
                    style={{ background: 'var(--ink-1)', color: 'var(--canvas)' }}
                    aria-label="New page"
                >
                    <Plus className="w-4 h-4" />
                    <span className="hidden md:block">New</span>
                    <ChevronDown className="w-3 h-3 hidden md:block" />
                </button>
            )}

            {/* Contextual + New menu */}
            {!wikiCrumb.isWiki && visibleQuickActions.length > 0 && (
                <div className="relative" ref={addRef}>
                    <button
                        onClick={() => setShowAddMenu(v => !v)}
                        data-testid="quick-add-btn"
                        className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 text-sm font-medium rounded-lg shadow-sm transition-colors"
                        style={{ background: 'var(--ink-1)', color: 'var(--canvas)' }}
                        aria-haspopup="menu"
                        aria-expanded={showAddMenu}
                    >
                        <Plus className="w-4 h-4" />
                        <span className="hidden md:block">New</span>
                        <ChevronDown className="w-3 h-3 hidden md:block" />
                    </button>
                    {showAddMenu && (
                        <div
                            role="menu"
                            className="absolute right-0 top-11 w-56 rounded-xl overflow-hidden z-40"
                            style={{
                                background: 'var(--surface)',
                                border: '1px solid var(--line)',
                                boxShadow: 'var(--shadow-pop)',
                            }}
                        >
                            <div className="py-1">
                                {visibleQuickActions.map(action => {
                                    const AI = action.icon;
                                    return (
                                        <button
                                            key={action.id}
                                            role="menuitem"
                                            onClick={() => { setShowAddMenu(false); navigate(action.path); }}
                                            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-ink-1 hover:bg-black/5 text-left"
                                        >
                                            <AI className="w-4 h-4 text-ink-2" />
                                            <span>{action.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* AI orb — opens the palette */}
            <button
                onClick={onOpenPalette}
                title="Ask AI"
                aria-label="Ask AI"
                className="hidden sm:inline-flex items-center justify-center relative"
                style={{
                    width: 32,
                    height: 32,
                    borderRadius: 16,
                    background: 'var(--ai-conic)',
                    padding: 2,
                    animation: 'orb-spin 9s linear infinite',
                }}
            >
                <span
                    className="block rounded-full w-full h-full"
                    style={{ background: 'var(--canvas)' }}
                />
            </button>

            {/* Notifications */}
            <div className="relative" ref={notifRef}>
                <button
                    onClick={toggleNotifs}
                    data-testid="notifications-btn"
                    className="relative p-2 rounded-lg hover:bg-black/5 text-ink-2 transition-colors"
                    aria-label="Notifications"
                >
                    <Bell className="w-5 h-5" />
                    {/* Degraded means the count could not be substantiated.
                        A list success can still write unread_total into
                        unreadCount (#715), so the status gates the number. */}
                    {notifStatus === 'ok' && unreadCount > 0 && (
                        <span
                            data-testid="notifications-badge"
                            className="absolute top-1 right-1 min-w-[16px] h-4 px-1 text-[10px] font-bold rounded-full flex items-center justify-center"
                            style={{ background: 'var(--bad)', color: '#fff' }}
                        >
                            {unreadCount}
                        </span>
                    )}
                </button>
                {showNotifs && (
                    <div
                        data-testid="notifications-panel"
                        className="absolute right-0 top-11 w-72 sm:w-80 rounded-xl overflow-hidden z-40 animate-fade-in"
                        style={{
                            background: 'var(--surface)',
                            border: '1px solid var(--line)',
                            boxShadow: 'var(--shadow-pop)',
                        }}
                    >
                        <div
                            className="flex items-center justify-between px-4 py-3"
                            style={{ borderBottom: '1px solid var(--line)' }}
                        >
                            <h3 className="font-semibold text-ink-1 text-sm">Notifications</h3>
                            <div className="flex items-center gap-2">
                                {notifStatus === 'ok' && unreadCount > 0 && (
                                    <button onClick={markAllRead} className="text-xs text-ink-2 hover:text-ink-1 font-medium">
                                        Mark all read
                                    </button>
                                )}
                                <button onClick={() => setShowNotifs(false)} className="p-1 rounded hover:bg-black/5" aria-label="Close">
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        </div>
                        {/* #715 — every unread entry renders, so the list is
                            taller (about 5–6 rows) but never taller than the
                            viewport; it scrolls inside the popover. */}
                        <div className="max-h-[min(28rem,70vh)] overflow-y-auto">
                            {notifStatus === 'degraded' ? (
                                <div className="p-6 text-center">
                                    <p className="text-ink-label text-sm mb-3">Notifications unavailable</p>
                                    <button
                                        onClick={() => { fetchUnread(); fetchNotifs(); }}
                                        data-testid="notifications-refresh"
                                        className="text-xs font-medium px-3 py-1.5 rounded-lg border hover:bg-black/5"
                                        style={{ borderColor: 'var(--line)', color: 'var(--ink-2)' }}
                                    >
                                        Refresh
                                    </button>
                                </div>
                            ) : notifications.length === 0 ? (
                                <div className="p-6 text-center text-ink-label text-sm" data-testid="notifications-empty">
                                    You&apos;re all caught up
                                </div>
                            ) : (
                                notifications.map(n => (
                                    <div
                                        key={n.notification_id}
                                        data-testid="notification-item"
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => handleNotifClick(n)}
                                        onKeyDown={(e) => {
                                            // #715 — the list can now hold 200+ rows; keep
                                            // every one reachable without a mouse.
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                handleNotifClick(n);
                                            }
                                        }}
                                        className="px-4 py-3 flex items-start gap-3 cursor-pointer"
                                        style={{
                                            borderBottom: '1px solid var(--line)',
                                            background: !n.is_read
                                                ? 'color-mix(in srgb, var(--ws, var(--ink-1)) 6%, transparent)'
                                                : 'transparent',
                                        }}
                                    >
                                        {!n.is_read && (
                                            <span
                                                className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                                                style={{ background: 'var(--ws, var(--ink-1))' }}
                                                aria-hidden="true"
                                            />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm text-ink-1 ${n.is_read ? 'font-medium' : 'font-semibold'}`}>
                                                {n.title}
                                                {n.type === 'email' && n.mail_state === 'trashed' && (
                                                    // #803 D1 — the email is in a Trash; the entry stays, read.
                                                    <span
                                                        className="ml-1.5 align-middle inline-block text-[10px] font-medium px-1.5 py-px rounded"
                                                        style={{ background: 'var(--line)', color: 'var(--ink-2)' }}
                                                        data-testid="notification-in-trash"
                                                    >
                                                        In Trash
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-xs text-ink-2 mt-0.5">{n.message}</p>
                                            {n.created_at && (
                                                <p className="text-[10px] text-ink-label mt-1">{new Date(n.created_at).toLocaleString()}</p>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        {notifStatus !== 'degraded' && bellMeta && bellMeta.truncated && (
                            <div
                                className="px-4 py-2.5 text-xs text-ink-2 flex items-center justify-between gap-2"
                                style={{ borderTop: '1px solid var(--line)' }}
                                data-testid="notifications-overflow"
                            >
                                <span>
                                    Showing the newest {bellMeta.unread_returned} of {bellMeta.unread_total} unread
                                </span>
                                <button
                                    type="button"
                                    onClick={() => { setShowNotifs(false); navigate('/inbox'); }}
                                    className="font-medium text-ink-1 hover:underline flex-shrink-0"
                                >
                                    Open inbox
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Help menu */}
            <div className="relative" ref={helpRef}>
                <button
                    onClick={() => setShowHelpMenu(v => !v)}
                    className="p-2 rounded-lg hover:bg-black/5 text-ink-2 hover:text-ink-1 transition-colors"
                    title="Help"
                    aria-label="Help menu"
                    aria-haspopup="menu"
                    aria-expanded={showHelpMenu}
                >
                    <HelpCircle className="w-5 h-5" />
                </button>
                {showHelpMenu && (
                    <div
                        role="menu"
                        className="absolute right-0 top-11 w-64 rounded-xl overflow-hidden z-40"
                        style={{
                            background: 'var(--surface)',
                            border: '1px solid var(--line)',
                            boxShadow: 'var(--shadow-pop)',
                        }}
                    >
                        <div className="py-1">
                            <button
                                role="menuitem"
                                onClick={() => { setShowHelpMenu(false); navigate(helpTarget); }}
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-ink-1 hover:bg-black/5 text-left"
                            >
                                <BookOpen className="w-4 h-4 text-ink-2" />
                                <span>Help Guide</span>
                            </button>
                            <button
                                role="menuitem"
                                onClick={() => { setShowHelpMenu(false); navigate('/feature-requests'); }}
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-ink-1 hover:bg-black/5 text-left"
                            >
                                <MessageSquareText className="w-4 h-4 text-ink-2" />
                                <span>Feature Requests</span>
                            </button>
                            <button
                                role="menuitem"
                                onClick={() => { setShowHelpMenu(false); navigate('/release-notes'); }}
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-ink-1 hover:bg-black/5 text-left"
                            >
                                <Rocket className="w-4 h-4 text-ink-2" />
                                <span>Release Notes</span>
                            </button>
                            {user?.org_name && (
                                <div
                                    className="px-3 py-2 text-[11px] text-ink-label"
                                    style={{ borderTop: '1px solid var(--line)', marginTop: 4 }}
                                >
                                    Organization: <span className="font-semibold text-ink-2">{user.org_name}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Mobile search overlay */}
            {showMobileSearch && (
                <div
                    className="absolute top-[52px] left-0 right-0 p-3 sm:hidden z-20"
                    style={{
                        background: 'var(--surface)',
                        borderBottom: '1px solid var(--line)',
                        boxShadow: 'var(--shadow-pop)',
                    }}
                >
                    <button
                        onClick={() => { setShowMobileSearch(false); onOpenPalette(); }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg text-ink-label border"
                        style={{
                            background: 'color-mix(in srgb, var(--ink-1) 3%, transparent)',
                            borderColor: 'var(--line)',
                        }}
                    >
                        <Search className="w-4 h-4" />
                        <span>Search…</span>
                    </button>
                </div>
            )}

            {/* Orb keyframes (inline so we don't touch global stylesheets) */}
            <style>{`
                @keyframes orb-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                @media (prefers-reduced-motion: reduce) {
                    button[title="Ask AI"] { animation: none !important; }
                }
            `}</style>
        </header>
    );
}
